var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { gql, useApolloClient, useReactiveVar } from "@apollo/client";
import React, { useState, useEffect, useMemo, useRef, useImperativeHandle, useCallback } from "react";
import _ from "underscore";
import Button, { ButtonVariant } from "./Button";
import LoadingScreen from "./LoadingScreen";
import ErrorScreen from "./ErrorScreen";
import { primaryActionLoadingVar, startPrimaryAction, completePrimaryAction, saveToFutureFulfillmentsVar, getCurrentUserId, currentShopifyProductVariantsVar, currencySymbolVar, currentBusinessHasTooManyProductsVar, tooManyProductsThreshold } from "../helpers/client";
import { benefitFulfillmentText, formattedPrice, majorToMinorPrice } from '../helpers/tools';
import { v4 as uuid } from "uuid";
import SvgX from "../icons/SvgX";
import ExpandableArea from "./ExpandableArea";
import { useChooseSubscriptionScreenQuery, useGetProductVariantsQuery, DeliveryMethod } from "../types";
import { DateTime } from "luxon";
import AddressInput from "./AddressInput";
import ChooseProductSelection from "./ChooseProductSelection";
import TextInput from "./TextInput";
import TextAreaInput from "./TextAreaInput";
import VariantOption from "./VariantOption";
// import { withfriendsMixpanel as mixpanel } from "../helpers/mixpanel"
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import { useSaveFulfillmentSettings, useUpdateUser, useUpdateVariantSelect } from "../cache/ChooseSubscriptionScreenFunctions";
import { customFilterSelectOption } from "@withfriendsco/wf-tools";
import DatePicker from "react-date-picker";
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import Select, { components as windowedComponents } from 'react-windowed-select';
import { components as selectComponents } from 'react-select';
import { BiSearch } from 'react-icons/bi';
import { HiExternalLink, HiOutlineCube, HiOutlineInformationCircle, HiOutlinePlus } from 'react-icons/hi';
const MOBILE_VIEWPORT_QUERY = "(max-width: 640px)";
const useMediaQuery = (query) => {
    const [matches, setMatches] = useState(() => {
        if (typeof window === "undefined" || typeof window.matchMedia === "undefined") {
            return false;
        }
        return window.matchMedia(query).matches;
    });
    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia === "undefined") {
            return;
        }
        const mediaQueryList = window.matchMedia(query);
        const handleChange = (event) => {
            setMatches(event.matches);
        };
        if (typeof mediaQueryList.addEventListener === "function") {
            mediaQueryList.addEventListener("change", handleChange);
        }
        else {
            mediaQueryList.addListener(handleChange);
        }
        setMatches(mediaQueryList.matches);
        return () => {
            if (typeof mediaQueryList.removeEventListener === "function") {
                mediaQueryList.removeEventListener("change", handleChange);
            }
            else {
                mediaQueryList.removeListener(handleChange);
            }
        };
    }, [query]);
    return matches;
};
const useIsMobileViewport = () => useMediaQuery(MOBILE_VIEWPORT_QUERY);
gql `
  fragment ChooseSubscriptionScreenAddress on Address {
    shippingName
    line1
    line2
    city
    state
    zip
    country
  }

  fragment ChooseSubscriptionScreenAddon on Addon {
    id
    description
    platformId
    platformName
    additionalCost {
      amount
    }
    applyDiscount
  }

  fragment ChooseSubscriptionScreenProduct on Product {
    id
    name
    platformId
    platformName
    platformVariantsOnlyMode
    curationValue
    variants {
      ...ChooseSubscriptionScreenVariant
    }
  }

  fragment UpdateVariantSelect on Fulfillment {
    variant {
      ...ChooseSubscriptionScreenVariant
    }
  }

  query ChooseSubscriptionScreen($id: ID!) {
    fulfillment(id: $id) {
      claimedAt
      id
      fulfillmentDate
      fulfillmentDateAdjustmentReason
      deliveryMethod
      variant {
        ...ChooseSubscriptionScreenVariant
      }
      productSelections {
        ...ChooseProductSelection
      }
      membership {
        id
        unfulfilledFulfillments: fulfillments(query: { isFulfilled: false}) {
          fulfillmentDate
          id
          benefit {
            id
          }
          address {
            ...ChooseSubscriptionScreenAddress
          }
          variant {
            ...ChooseSubscriptionScreenVariant
          }
          addons {
            ...ChooseSubscriptionScreenAddon
          }
          productSelections {
            ...ChooseProductSelection
          }
          shippingNotes
        }
        user {
          id
          firstName
          lastName
          phoneNumber
        }
        purchaser {
          id
          firstName
          lastName
        }
        tier {
          id
          name
          discounts {
            scope
            percentage
          }
          subscriptionBenefits {
            shippingIncluded
          }
        }
      }
      addons {
        ...ChooseSubscriptionScreenAddon
      }
      shippingNotes
      address {
        ...ChooseSubscriptionScreenAddress
      }
      isGift
      benefit {
        __typename
        id
        name
        ...on SubscriptionBenefit {
          isAddonOnly
          deliveredToYour
          shippingCost {
            amount
          }
          pickupEnabled
          shippingEnabled
          shippingIncluded
          shippingNotesInstructions
          shippingFrequency {
            period
          }
          deliveryFrequency
          delivery
          membershipTiers {
            members(status: Active){
              id
            }
            plans {
              kind
              price {
                amount
              }
            }
          }
          business {
            id
            shopifyVersion
            enableProductSwapping
            forceStripePayments
          }
          claimable
        }
        ... on DriveBenefit{
          deliveredToYour
          shippingNotesInstructions
          shippingCost {
            amount
          }
          pickupEnabled
          shippingEnabled
          claimable
          business {
            id
            shopifyVersion
            forceStripePayments
          }
        }
        ... on WelcomeGiftBenefit{
          deliveredToYour
          shippingNotesInstructions
          shippingCost {
            amount
          }
          pickupEnabled
          shippingEnabled
          claimable
          business {
            id
            shopifyVersion
            forceStripePayments
          }
        }
        products {
          ...ChooseSubscriptionScreenProduct
        }
        variants {
          ...ChooseSubscriptionScreenVariant
        }
        addons {
          id
          description
          additionalCost {
            amount
          }
          applyDiscount
          platformId
          platformName
        }
      }
      defaultCurations {
        productSelections {
          product {
            id
          }
          variant{ 
            id
          }
        }
        platformLineItems {
          quantity
          platformProductVariantId
        }
      }
      platformLineItems {
        quantity
        platformProductVariantId
        swapped
      }
      curated
    }
  }
`;
const deliveryPeriod = (subscriptionBox) => {
    var _a;
    if (!!subscriptionBox.deliveryFrequency && !!((_a = subscriptionBox.shippingFrequency) === null || _a === void 0 ? void 0 : _a.period)) {
        let suffix = (subscriptionBox.deliveryFrequency > 1 ? subscriptionBox.deliveryFrequency + ' ' : '')
            + subscriptionBox.shippingFrequency.period.toLowerCase()
            + (subscriptionBox.deliveryFrequency > 1 ? 's' : '');
        // Handle special cases
        switch (suffix) {
            case "4 quarters":
            case "12 months":
            case "52 weeks":
                suffix = 'year';
                break;
            case "quarter":
            case "3 months":
            case "12 weeks":
            case "13 weeks":
                suffix = '3 months';
                break;
            case "4 weeks":
                suffix = 'month';
                break;
        }
        return suffix;
    }
    else {
        let suffixMap = {
            'annual': 'year',
            'quarterly': '3 months',
            'monthly': 'month',
            'biweekly': '2 weeks',
            'weekly': 'week'
        };
        return suffixMap[subscriptionBox.delivery.toLowerCase()];
    }
};
const ChooseSubscriptionScreen = React.forwardRef(({ id, saveCallback, showCancelButton = false, fromFulfillmentScreen = false, isAfterPurchase = false, hasWelcomeGift = false, isEmbedded = false, headerText = null, isUserUpdateHandledByParent = false, onDeliveryMethodChange = (method) => { } }, ref) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26, _27, _28, _29, _30, _31, _32, _33, _34, _35, _36, _37, _38, _39, _40, _41, _42, _43, _44, _45, _46, _47, _48, _49, _50, _51, _52, _53, _54, _55, _56, _57, _58, _59, _60, _61, _62, _63, _64, _65, _66, _67, _68, _69, _70, _71, _72, _73, _74, _75;
    const namespace = '_' + uuid();
    const currentUserId = getCurrentUserId();
    const saveToFutureFulfillments = useReactiveVar(saveToFutureFulfillmentsVar);
    const updateVariantSelect = useUpdateVariantSelect(id);
    const [highlightedFields, setHighlightedFields] = useState([]);
    const [saveMessage, setSaveMessage] = useState(_jsx("span", {}));
    const [originalPlatformLineItems, setOriginalPlatformLineItems] = useState(null);
    const primaryActionLoading = useReactiveVar(primaryActionLoadingVar);
    const client = useApolloClient();
    const { data, loading, error } = useChooseSubscriptionScreenQuery({
        variables: { id },
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-only'
    });
    const [localAddress, setLocalAddress] = useState(null);
    const [localDeliveryMethod, setLocalDeliveryMethod] = useState(null);
    useEffect(() => {
        if (data === null || data === void 0 ? void 0 : data.fulfillment) {
            if (localDeliveryMethod === null && data.fulfillment.deliveryMethod) {
                setLocalDeliveryMethod(data.fulfillment.deliveryMethod);
                onDeliveryMethodChange(data.fulfillment.deliveryMethod);
            }
            if (!localAddress && data.fulfillment.address) {
                setLocalAddress(data.fulfillment.address);
            }
        }
    }, [data === null || data === void 0 ? void 0 : data.fulfillment, localAddress, localDeliveryMethod, onDeliveryMethodChange]);
    const fulfillmentDate = DateTime.fromSQL((_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.fulfillmentDate);
    const [originalFulfillmentDate, setOriginalFulfillmentDate] = useState(null);
    const purchaserId = (_d = (_c = (_b = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _b === void 0 ? void 0 : _b.membership) === null || _c === void 0 ? void 0 : _c.purchaser) === null || _d === void 0 ? void 0 : _d.id.toString();
    const memberId = (_g = (_f = (_e = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _e === void 0 ? void 0 : _e.membership) === null || _f === void 0 ? void 0 : _f.user) === null || _g === void 0 ? void 0 : _g.id.toString();
    const currentShopifyVersion = (_k = (_j = (_h = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _h === void 0 ? void 0 : _h.benefit) === null || _j === void 0 ? void 0 : _j.business) === null || _k === void 0 ? void 0 : _k.shopifyVersion;
    const currentBusinessHasTooManyProducts = currentBusinessHasTooManyProductsVar();
    const currentBusinessId = (_o = (_m = (_l = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _l === void 0 ? void 0 : _l.benefit) === null || _m === void 0 ? void 0 : _m.business) === null || _o === void 0 ? void 0 : _o.id;
    const isMobileViewport = useIsMobileViewport();
    const firstCurationLoadHasOccurred = useRef(false);
    const selectContainerRef = useRef(null);
    const selectInstanceRef = useRef(null);
    const menuScrollCleanupRef = useRef(null);
    const resetCuration = () => {
        if (!defaultCuration)
            return;
        const nextPlatformLineItems = ((defaultCuration === null || defaultCuration === void 0 ? void 0 : defaultCuration.platformLineItems) || [])
            .filter((lineItem) => Boolean(lineItem))
            .map((lineItem) => {
            var _a, _b;
            return ({
                __typename: (_a = lineItem.__typename) !== null && _a !== void 0 ? _a : "PlatformLineItem",
                platformProductVariantId: lineItem.platformProductVariantId,
                quantity: lineItem.quantity,
                swapped: (_b = lineItem.swapped) !== null && _b !== void 0 ? _b : false,
            });
        });
        client.cache.modify({
            id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
            fields: {
                platformLineItems: () => {
                    return nextPlatformLineItems;
                },
            },
        });
        setOriginalPlatformLineItems(nextPlatformLineItems);
    };
    const hasSelectionChanged = () => {
        var _a;
        if (originalPlatformLineItems === null)
            return false;
        const currentPlatformLineItems = ((_a = client.readFragment({
            id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
            fragment: gql `
        fragment CurrentPlatformLineItems on Fulfillment {
          platformLineItems {
            quantity
            platformProductVariantId
          }
        }
      `
        })) === null || _a === void 0 ? void 0 : _a.platformLineItems) || [];
        return !_.isEqual(currentPlatformLineItems.map(({ quantity, platformProductVariantId }) => ({ quantity, platformProductVariantId })), originalPlatformLineItems.map(({ quantity, platformProductVariantId }) => ({ quantity, platformProductVariantId })));
    };
    const { data: dataProducts, error: errorProducts, stopPolling: stopPollingProducts } = useGetProductVariantsQuery({
        variables: {
            id: currentBusinessId,
        },
        pollInterval: 1000,
        skip: !currentBusinessId || currentShopifyVersion != 2 || currentBusinessHasTooManyProducts
    });
    useEffect(() => {
        var _a;
        if (((_a = dataProducts === null || dataProducts === void 0 ? void 0 : dataProducts.business) === null || _a === void 0 ? void 0 : _a.shopifyProductsCount) > tooManyProductsThreshold) {
            currentBusinessHasTooManyProductsVar(true);
            stopPollingProducts();
        }
    }, [(_p = dataProducts === null || dataProducts === void 0 ? void 0 : dataProducts.business) === null || _p === void 0 ? void 0 : _p.shopifyProductsCount]);
    useEffect(() => {
        var _a;
        if (((_a = dataProducts === null || dataProducts === void 0 ? void 0 : dataProducts.business) === null || _a === void 0 ? void 0 : _a.shopifyProductVariants) != null) {
            stopPollingProducts();
            currentShopifyProductVariantsVar(dataProducts.business.shopifyProductVariants);
        }
    }, [(_q = dataProducts === null || dataProducts === void 0 ? void 0 : dataProducts.business) === null || _q === void 0 ? void 0 : _q.shopifyProductVariants]);
    useEffect(() => {
        var _a;
        if (((_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.platformLineItems) && originalPlatformLineItems === null) {
            const nonNullPlatformLineItems = data.fulfillment.platformLineItems.filter((lineItem) => Boolean(lineItem));
            setOriginalPlatformLineItems(nonNullPlatformLineItems);
        }
    }, [(_r = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _r === void 0 ? void 0 : _r.platformLineItems, originalPlatformLineItems]);
    // GPT 
    function useDeepMemo(value) {
        const ref = useRef();
        if (!_.isEqual(value, ref.current)) {
            ref.current = value;
        }
        return ref.current;
    }
    const memoizedProductSelections = useDeepMemo((_s = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _s === void 0 ? void 0 : _s.productSelections);
    useEffect(() => {
        var _a;
        if (!!((_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.productSelections)) {
            if (firstCurationLoadHasOccurred.current)
                resetCuration();
            else
                firstCurationLoadHasOccurred.current = true;
        }
    }, [memoizedProductSelections]);
    const Control = (props) => (_jsxs(selectComponents.Control, Object.assign({}, props, { children: [_jsx(BiSearch, { "aria-hidden": true, className: "ml-3 mr-2 text-wfGray-500", size: 18, style: { flexShrink: 0 } }), props.children] })));
    const Placeholder = (props) => (_jsx(selectComponents.Placeholder, Object.assign({}, props, { children: "Search by product name or vendor" })));
    const NoOptionsMessage = (props) => (_jsx(selectComponents.NoOptionsMessage, Object.assign({}, props, { children: "No products match your search. Try a different name or vendor." })));
    const MenuList = (props) => (_jsxs(_Fragment, { children: [_jsx("div", { className: "px-3 py-2 text-xs uppercase tracking-wide text-wfGray-500 bg-wfGray-50 border-b border-wfGray-200", children: "Start typing to filter the list or scroll to browse everything." }), _jsx(windowedComponents.MenuList, Object.assign({}, props))] }));
    const handleMenuOpen = useCallback(() => {
        var _a;
        if (!isMobileViewport) {
            return;
        }
        if (typeof window === "undefined") {
            return;
        }
        if (menuScrollCleanupRef.current) {
            menuScrollCleanupRef.current();
            menuScrollCleanupRef.current = null;
        }
        const node = selectContainerRef.current;
        if (!node) {
            return;
        }
        const cleanupFns = [];
        let lastEmbedOffset = null;
        const adjustScroll = () => {
            var _a;
            if (typeof node.scrollIntoView === "function") {
                try {
                    node.scrollIntoView({ block: "start", behavior: "smooth" });
                }
                catch (error) {
                    node.scrollIntoView();
                }
            }
            const rect = node.getBoundingClientRect();
            const isEmbedded = (() => {
                try {
                    return typeof window !== "undefined" && window.self !== window.top;
                }
                catch (error) {
                    return true;
                }
            })();
            if (isEmbedded) {
                if (typeof window !== "undefined" &&
                    typeof ((_a = window.parent) === null || _a === void 0 ? void 0 : _a.postMessage) === "function") {
                    const origin = (() => {
                        var _a, _b;
                        try {
                            return (_b = (_a = window.location) === null || _a === void 0 ? void 0 : _a.origin) !== null && _b !== void 0 ? _b : "";
                        }
                        catch (error) {
                            return "";
                        }
                    })();
                    const offset = Math.round(rect.top - 24);
                    try {
                        if (lastEmbedOffset !== offset) {
                            window.parent.postMessage({
                                action: "wf-subscription-scroll",
                                offset,
                                origin,
                            }, "*");
                            lastEmbedOffset = offset;
                        }
                    }
                    catch (error) {
                        // noop: best-effort message to host page
                    }
                }
                return;
            }
            const currentScrollY = typeof window.pageYOffset === "number"
                ? window.pageYOffset
                : typeof window.scrollY === "number"
                    ? window.scrollY
                    : typeof document !== "undefined" && document.documentElement
                        ? document.documentElement.scrollTop
                        : 0;
            const target = Math.max(0, currentScrollY + rect.top - 24);
            if (typeof window.scrollTo === "function") {
                try {
                    window.scrollTo({ top: target, left: 0, behavior: "auto" });
                }
                catch (error) {
                    window.scrollTo(0, target);
                }
            }
            else if (typeof document !== "undefined") {
                if (typeof document.documentElement !== "undefined") {
                    document.documentElement.scrollTop = target;
                }
                if (typeof document.body !== "undefined") {
                    document.body.scrollTop = target;
                }
            }
        };
        const scheduleAdjust = () => {
            if (typeof window.requestAnimationFrame === "function") {
                window.requestAnimationFrame(() => {
                    if (typeof window.setTimeout === "function") {
                        window.setTimeout(adjustScroll, 50);
                    }
                    else {
                        adjustScroll();
                    }
                });
            }
            else if (typeof window.setTimeout === "function") {
                window.setTimeout(adjustScroll, 50);
            }
            else {
                adjustScroll();
            }
        };
        scheduleAdjust();
        const viewport = (_a = window.visualViewport) !== null && _a !== void 0 ? _a : null;
        if (viewport && typeof viewport.addEventListener === "function") {
            const handleViewportChange = () => {
                scheduleAdjust();
            };
            viewport.addEventListener("resize", handleViewportChange);
            viewport.addEventListener("scroll", handleViewportChange);
            cleanupFns.push(() => {
                viewport.removeEventListener("resize", handleViewportChange);
                viewport.removeEventListener("scroll", handleViewportChange);
            });
        }
        if (typeof window.setTimeout === "function") {
            const fallbackTimeouts = [150, 300, 450].map((delay) => window.setTimeout(() => {
                scheduleAdjust();
            }, delay));
            cleanupFns.push(() => {
                fallbackTimeouts.forEach((timeoutId) => {
                    window.clearTimeout(timeoutId);
                });
            });
        }
        menuScrollCleanupRef.current = () => {
            cleanupFns.forEach((cleanup) => cleanup());
            menuScrollCleanupRef.current = null;
        };
    }, [isMobileViewport]);
    const handleMenuClose = useCallback(() => {
        if (menuScrollCleanupRef.current) {
            menuScrollCleanupRef.current();
        }
    }, []);
    useEffect(() => {
        return () => {
            if (menuScrollCleanupRef.current) {
                menuScrollCleanupRef.current();
            }
        };
    }, []);
    const Option = (props) => {
        var _a;
        if (!isMobileViewport) {
            return _jsx(windowedComponents.Option, Object.assign({}, props));
        }
        const { isSelected, isDisabled, data } = props;
        const currentOptions = props.selectProps.value;
        const normalizedCurrentOptions = Array.isArray(currentOptions)
            ? [...currentOptions]
            : currentOptions
                ? [currentOptions]
                : [];
        const targetAllowsSelection = (eventTarget) => {
            return (eventTarget instanceof HTMLElement &&
                Boolean(eventTarget.closest('[data-mobile-option-action="true"]')));
        };
        const createGuardedHandler = (handler) => {
            return (event) => {
                if (targetAllowsSelection(event.target)) {
                    handler === null || handler === void 0 ? void 0 : handler(event);
                    return;
                }
                event.preventDefault();
                event.stopPropagation();
            };
        };
        const adjustedInnerProps = Object.assign(Object.assign({}, props.innerProps), { onMouseDown: createGuardedHandler(props.innerProps.onMouseDown), onClick: createGuardedHandler(props.innerProps.onClick), onTouchStart: createGuardedHandler(props.innerProps.onTouchStart), onTouchEnd: createGuardedHandler(props.innerProps.onTouchEnd) });
        adjustedInnerProps.className = [
            (_a = props.innerProps.className) !== null && _a !== void 0 ? _a : "",
            "bg-white px-3 py-4 border-b border-wfGray-200 cursor-default"
        ]
            .filter(Boolean)
            .join(" ");
        const handleAddClick = (event) => {
            var _a;
            event.preventDefault();
            event.stopPropagation();
            if (isSelected || isDisabled) {
                return;
            }
            const alreadySelected = normalizedCurrentOptions.some((option) => option.value === data.value);
            if (alreadySelected) {
                return;
            }
            props.selectOption(data);
            (_a = selectInstanceRef.current) === null || _a === void 0 ? void 0 : _a.blur();
        };
        const addButtonClassName = [
            "flex w-full items-center justify-center gap-2 border px-4 py-3 text-sm font-semibold uppercase tracking-wide transition rounded-none",
            isSelected || isDisabled
                ? "cursor-default border-wfGray-200 bg-wfGray-100 text-wfGray-500"
                : "border-black bg-black text-white hover:opacity-90"
        ].join(" ");
        return (_jsx(windowedComponents.Option, Object.assign({}, props, { innerProps: adjustedInnerProps, isDisabled: isDisabled, isSelected: isSelected, children: _jsxs("div", { className: "flex flex-col gap-4", children: [props.children, _jsxs("button", { type: "button", "data-mobile-option-action": "true", onClick: handleAddClick, disabled: isSelected || isDisabled, "aria-label": isSelected
                            ? `${data.displayName} is already in your box`
                            : `Add ${data.displayName} to your box`, className: addButtonClassName, children: [!isSelected && _jsx(HiOutlinePlus, { className: "text-lg" }), _jsx("span", { className: "block", children: isSelected ? "Added to your box" : "Add to your box" })] })] }) })));
    };
    const currentShopifyProductVariants = useReactiveVar(currentShopifyProductVariantsVar);
    const currencySymbol = currencySymbolVar();
    const productSearchHelperId = useMemo(() => `product-search-helper-${id}`, [id]);
    // Get default platform line items by searching default Curations
    const defaultCuration = (_u = (_t = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _t === void 0 ? void 0 : _t.defaultCurations) === null || _u === void 0 ? void 0 : _u.find((defaultCuration) => {
        var _a;
        const a = (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.productSelections.map((productSelection) => {
            var _a;
            return {
                product: productSelection.product.id,
                variant: (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.id
            };
        }).sort((a, b) => a.product.localeCompare(b.product));
        const b = defaultCuration.productSelections.map((productSelection) => {
            var _a;
            return {
                product: productSelection.product.id,
                variant: (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.id
            };
        }).sort((a, b) => a.product.localeCompare(b.product));
        return _.isEqual(a, b);
    });
    const defaultCurationPlatformLineItems = (_w = (_v = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _v === void 0 ? void 0 : _v.defaultCurations) === null || _w === void 0 ? void 0 : _w.flatMap((defaultCuration) => {
        return defaultCuration.platformLineItems || [];
    });
    let aggregateCurationValue = (_z = (_y = (_x = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _x === void 0 ? void 0 : _x.benefit) === null || _y === void 0 ? void 0 : _y.products) === null || _z === void 0 ? void 0 : _z.reduce((acc, product) => {
        acc += product.curationValue;
        return acc;
    }, 0);
    const discounts = (_2 = (_1 = (_0 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _0 === void 0 ? void 0 : _0.membership) === null || _1 === void 0 ? void 0 : _1.tier) === null || _2 === void 0 ? void 0 : _2.discounts;
    let retailDiscount = { scope: "RETAIL", percentage: 0 };
    if (discounts) {
        const newDiscount = discounts.find((d) => d.scope === "RETAIL");
        if (newDiscount) {
            retailDiscount = newDiscount;
        }
    }
    const discountAddon = (cost) => {
        return { amount: cost.amount * (1.0 - 0.01 * retailDiscount.percentage) };
    };
    const undiscountAddon = (cost) => {
        return { amount: cost.amount / (1.0 - 0.01 * retailDiscount.percentage) };
    };
    const discountedAggregateCurationValue = discountAddon({ amount: aggregateCurationValue }).amount;
    let defaultCurationValue = ((_3 = defaultCuration === null || defaultCuration === void 0 ? void 0 : defaultCuration.platformLineItems) === null || _3 === void 0 ? void 0 : _3.reduce((acc, platformLineItem) => {
        const matchedOption = currentShopifyProductVariants === null || currentShopifyProductVariants === void 0 ? void 0 : currentShopifyProductVariants.find(productVariant => productVariant.id.split("/").pop() == platformLineItem.platformProductVariantId);
        acc += parseFloat(matchedOption === null || matchedOption === void 0 ? void 0 : matchedOption.price) || 0;
        return acc;
    }, 0)) || 0;
    let discountedDefaultCurationValue = discountAddon({ amount: defaultCurationValue }).amount;
    discountedDefaultCurationValue = discountedDefaultCurationValue > discountedAggregateCurationValue ? discountedDefaultCurationValue : discountedAggregateCurationValue;
    const price = (_5 = (_4 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _4 === void 0 ? void 0 : _4.platformLineItems) === null || _5 === void 0 ? void 0 : _5.reduce((acc, platformLineItem) => {
        const matchedOption = currentShopifyProductVariants === null || currentShopifyProductVariants === void 0 ? void 0 : currentShopifyProductVariants.find(productVariant => productVariant.id.split("/").pop() == platformLineItem.platformProductVariantId);
        acc += parseFloat(matchedOption === null || matchedOption === void 0 ? void 0 : matchedOption.price) || 0;
        return acc;
    }, 0);
    const discountedPrice = discountAddon({ amount: price }).amount;
    const discountedAddonAmount = (discountedPrice - discountedDefaultCurationValue) > 0 ? (discountedPrice - discountedDefaultCurationValue) : 0;
    const subscriptionBenefitHasShippingIncluded = (_9 = (_8 = (_7 = (_6 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _6 === void 0 ? void 0 : _6.membership) === null || _7 === void 0 ? void 0 : _7.tier) === null || _8 === void 0 ? void 0 : _8.subscriptionBenefits) === null || _9 === void 0 ? void 0 : _9.every(benefit => benefit.shippingIncluded);
    const options = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g;
        if (!currentShopifyProductVariants || currentShopifyProductVariants.length === 0) {
            return [];
        }
        // TODO - I'm coding this live so i don't have time to do this right, but the right way to do this is to set this as an unrelated effect and have it happen first.
        const stupidDefaultCuration = (_b = (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.defaultCurations) === null || _b === void 0 ? void 0 : _b.find((defaultCuration) => {
            var _a;
            const a = (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.productSelections.map((productSelection) => {
                var _a;
                return {
                    product: productSelection.product.id,
                    variant: (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.id
                };
            }).sort((a, b) => a.product.localeCompare(b.product));
            const b = defaultCuration.productSelections.map((productSelection) => {
                var _a;
                return {
                    product: productSelection.product.id,
                    variant: (_a = productSelection.variant) === null || _a === void 0 ? void 0 : _a.id
                };
            }).sort((a, b) => a.product.localeCompare(b.product));
            return _.isEqual(a, b);
        });
        //@ts-ignore
        let minimumInventory = (_e = (_d = (_c = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _c === void 0 ? void 0 : _c.benefit) === null || _d === void 0 ? void 0 : _d.membershipTiers) === null || _e === void 0 ? void 0 : _e.reduce((count, tier) => { var _a; return count + ((_a = tier.members) === null || _a === void 0 ? void 0 : _a.length); }, 0);
        minimumInventory = 4; // below, we pass all products already in the selection, so this is just for customizations 
        let swapCollection = null;
        switch ((_g = (_f = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _f === void 0 ? void 0 : _f.benefit) === null || _g === void 0 ? void 0 : _g.id) {
            // @ts-ignore 
            case "15184329": // Psychic damage: BLK & GRN
                minimumInventory = 8;
            case "15183415": // BLK + GRN all tiers
            case "15185990":
                swapCollection = "Shop In Stock";
                break;
            case "20385356": // Psychic damage: Silver Sprocket
                swapCollection = "Downloads";
                break;
        }
        return currentShopifyProductVariants.filter(pv => {
            var _a, _b, _c;
            const platformProductVariantId = pv.id.split("/").pop();
            const isAlreadyInSelection = (_b = (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.platformLineItems) === null || _b === void 0 ? void 0 : _b.find((platformLineItem) => platformLineItem.platformProductVariantId == platformProductVariantId);
            const isInDefaultCuration = defaultCurationPlatformLineItems === null || defaultCurationPlatformLineItems === void 0 ? void 0 : defaultCurationPlatformLineItems.some(item => item.platformProductVariantId === platformProductVariantId);
            const doesNotTrackInventory = !pv.inventoryTracked;
            const hasMinimumInventory = pv.inventory >= minimumInventory;
            const inSwapCollection = !swapCollection || ((_c = pv.collections) === null || _c === void 0 ? void 0 : _c.some(collection => collection == swapCollection));
            const hasPrice = !!parseFloat(pv.price);
            const hasWebLink = !pv.onlineStoreUrl.includes('shopifypreview');
            return isInDefaultCuration || isAlreadyInSelection || (hasWebLink && hasPrice && (hasMinimumInventory || doesNotTrackInventory) && inSwapCollection);
        }).map(productVariant => {
            var _a, _b, _c;
            // const isAlreadyInSelection = data?.fulfillment?.platformLineItems?.find((platformLineItem) => platformLineItem.platformProductVariantId == productVariant.id.split("/").pop())
            const platformProductVariantId = productVariant.id.split("/").pop();
            const isAlreadyInSelection = (_b = (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.platformLineItems) === null || _b === void 0 ? void 0 : _b.find((platformLineItem) => platformLineItem.platformProductVariantId == platformProductVariantId);
            const isInDefaultCuration = (_c = stupidDefaultCuration === null || stupidDefaultCuration === void 0 ? void 0 : stupidDefaultCuration.platformLineItems) === null || _c === void 0 ? void 0 : _c.find((platformLineItem) => platformLineItem.platformProductVariantId == platformProductVariantId);
            const productVariantPrice = parseFloat(productVariant.price);
            const discountedProductVariantPrice = discountAddon({ amount: productVariantPrice }).amount;
            let discountedAddonPriceTotal = 0;
            let discountedAddonPriceForProductVariant = 0;
            let addonPriceTotal = 0;
            let addonPriceForProductVariant = 0;
            const possibleDiscountedAddonTotal = discountedPrice + discountedProductVariantPrice - discountedDefaultCurationValue;
            if (possibleDiscountedAddonTotal > 0) {
                discountedAddonPriceTotal = possibleDiscountedAddonTotal;
                discountedAddonPriceForProductVariant = (discountedAddonPriceTotal < discountedProductVariantPrice) ? discountedAddonPriceTotal : discountedProductVariantPrice;
                addonPriceTotal = undiscountAddon({ amount: discountedAddonPriceTotal }).amount;
                addonPriceForProductVariant = undiscountAddon({ amount: discountedAddonPriceForProductVariant }).amount;
            }
            const hasAdditionalCost = discountedAddonPriceForProductVariant > 0;
            const discountedPriceCopy = hasAdditionalCost ? formattedPrice(majorToMinorPrice(discountedAddonPriceForProductVariant)) : null;
            const originalPriceCopy = hasAdditionalCost && retailDiscount.percentage > 0 && addonPriceForProductVariant
                ? formattedPrice(majorToMinorPrice(addonPriceForProductVariant))
                : null;
            const priceSummaryLong = hasAdditionalCost
                ? `Adds ${discountedPriceCopy} to this delivery`
                : "Included in your membership";
            return {
                id: productVariant.id.split("/").pop(),
                sku: productVariant.sku,
                productId: productVariant.productId.split("/").pop(),
                displayName: productVariant.displayName,
                vendor: productVariant.vendor,
                productTitle: productVariant.productTitle,
                variantTitle: productVariant.variantTitle,
                description: productVariant.description,
                imageUrl: productVariant.imageUrl,
                hasAdditionalCost,
                priceSummaryLong,
                originalPriceCopy,
                discountedPriceCopy,
                isInDefaultCuration: !!isInDefaultCuration,
                isAlreadyInSelection: !!isAlreadyInSelection,
                value: productVariant.id.split("/").pop() + "," + productVariant.displayName,
                label: (_jsxs("div", { className: "wf-option-wrapper flex items-stretch gap-4 border-b border-wfGray-200 bg-white px-3 py-4" +
                        (isInDefaultCuration ? " wf-in-default-curation" : ""), children: [_jsx("div", { className: "flex h-20 w-20 flex-none items-center justify-center border border-wfGray-200 bg-white", children: productVariant.imageUrl ? (_jsx("img", { src: productVariant.imageUrl, alt: productVariant.displayName, className: "h-full w-full object-cover" })) : (_jsx("span", { className: "px-2 text-center text-xs uppercase tracking-wide text-wfGray-400", children: "No image" })) }), _jsxs("div", { className: "flex min-w-0 flex-1 flex-col gap-3", children: [_jsxs("div", { className: "flex items-start gap-3", children: [_jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "text-base font-semibold leading-snug text-wfGray-900", children: productVariant.displayName }), productVariant.vendor && (_jsxs("p", { className: "mt-1 text-sm text-wfGray-600", children: ["by ", productVariant.vendor] }))] }), productVariant.onlineStoreUrl && (_jsx("button", { type: "button", className: "inline-flex h-8 w-8 flex-none items-center justify-center text-wfGray-600 transition hover:text-wfGray-900", "aria-label": `View ${productVariant.displayName} in a new tab`, onPointerDown: (event) => {
                                                event.stopPropagation();
                                                event.preventDefault();
                                                window.open(productVariant.onlineStoreUrl, "_blank");
                                            }, children: _jsx(HiExternalLink, { className: "text-lg" }) }))] }), _jsx("div", { className: "text-sm font-medium text-wfGray-900", children: hasAdditionalCost ? (_jsxs("div", { className: "flex flex-wrap items-baseline gap-2", children: [originalPriceCopy && (_jsxs("span", { className: "line-through text-wfGray-500", children: ["+", originalPriceCopy] })), _jsxs("span", { className: "text-wfRed-600", children: ["+", discountedPriceCopy] }), _jsx("span", { className: "text-xs font-medium uppercase tracking-wide text-wfGray-600", children: "to this delivery" })] })) : (_jsx("span", { className: "bg-[#f6f4f4] px-2 py-1 text-xs font-semibold uppercase tracking-wide text-wfGray-700", children: "Included in your membership" })) }), _jsxs("div", { className: "flex flex-wrap gap-2 text-xs font-medium text-wfGray-600", children: [isAlreadyInSelection && (_jsx("span", { className: "border border-wfGray-300 px-2 py-1 uppercase tracking-wide", children: "In your box" })), isInDefaultCuration && (_jsx("span", { className: "border border-salmon-500 px-2 py-1 uppercase tracking-wide text-salmon-600", children: "Recommended" }))] })] })] }))
            };
        });
    }, [
        (_10 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _10 === void 0 ? void 0 : _10.platformLineItems,
        currentShopifyProductVariants,
        (_11 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _11 === void 0 ? void 0 : _11.productSelections,
        discountedDefaultCurationValue,
        discountedPrice,
        defaultCurationPlatformLineItems,
        retailDiscount === null || retailDiscount === void 0 ? void 0 : retailDiscount.percentage,
        (_12 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _12 === void 0 ? void 0 : _12.defaultCurations
    ]);
    const isAddonOnly = ((_14 = (_13 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _13 === void 0 ? void 0 : _13.benefit) === null || _14 === void 0 ? void 0 : _14.id) && ["22624156", "22945597"].includes((_16 = (_15 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _15 === void 0 ? void 0 : _15.benefit) === null || _16 === void 0 ? void 0 : _16.id);
    // Psychic Damage: Casita Bookstore
    useEffect(() => {
        if (isAddonOnly) {
            document.querySelectorAll('.wf-option-wrapper.wf-in-default-curation').forEach(x => { var _a; return (_a = x.parentElement.parentElement.querySelector('[aria-label^="Remove"]')) === null || _a === void 0 ? void 0 : _a.classList.add('hidden'); });
            document.querySelectorAll('.wf-option-wrapper:not(.wf-in-default-curation)').forEach(x => { var _a; return (_a = x.parentElement.parentElement.querySelector('[aria-label^="Remove"]')) === null || _a === void 0 ? void 0 : _a.classList.remove('hidden'); });
        }
    }, [(_17 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _17 === void 0 ? void 0 : _17.platformLineItems, currentShopifyProductVariants, isAddonOnly]);
    const selectedOptions = useMemo(() => {
        var _a;
        if (!(options === null || options === void 0 ? void 0 : options.length)) {
            return [];
        }
        return (((_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.platformLineItems) || [])
            .map((platformLineItem) => {
            const option = options.find(option => option.value.split(",")[0].split("/").pop() == platformLineItem.platformProductVariantId);
            if (!option)
                return null;
            const isDefaultSelection = defaultCurationPlatformLineItems === null || defaultCurationPlatformLineItems === void 0 ? void 0 : defaultCurationPlatformLineItems.some(defaultLineItem => defaultLineItem.platformProductVariantId === platformLineItem.platformProductVariantId);
            return Object.assign(Object.assign({}, option), { isDefaultSelection });
        })
            .filter(Boolean);
    }, [
        (_18 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _18 === void 0 ? void 0 : _18.platformLineItems,
        options,
        defaultCurationPlatformLineItems,
    ]);
    useEffect(() => {
        // mixpanel.track("Viewed Choose Subscription Screen")
    }, []);
    useEffect(() => {
        // @ts-ignore
        for (const el of document.getElementsByClassName("wf-button-back")) {
            if (el instanceof HTMLElement) {
                el.style.display = "none";
            }
        }
    }, []);
    useEffect(() => {
        if (!originalFulfillmentDate && fulfillmentDate.isValid)
            setOriginalFulfillmentDate(fulfillmentDate);
    }, [fulfillmentDate]);
    const { wrapped: updateUser } = useUpdateUser({ userId: memberId });
    const { wrapped: saveSettings } = useSaveFulfillmentSettings({ id, includeDate: fromFulfillmentScreen });
    useImperativeHandle(ref, () => ({
        save: async (options) => {
            const { sharedAddress, shippingNotes } = options || {};
            if (shippingNotes !== undefined) {
                client.cache.modify({
                    id: `Fulfillment:${id}`,
                    fields: {
                        shippingNotes: () => shippingNotes
                    }
                });
            }
            return await saveFulfillment(sharedAddress, true);
        },
        checkRequiredFields: () => {
            return checkRequiredFields(true);
        }
    }));
    const closeSaveMessage = () => {
        setSaveMessage(_jsx("span", {}));
    };
    const checkRequiredFields = (isEmbedded = false, addressOverride) => {
        var _a, _b, _c;
        if (isAddonOnlyBenefit && ((data === null || data === void 0 ? void 0 : data.fulfillment.addons) || []).length === 0) {
            return [];
        }
        const currentDeliveryMethod = localDeliveryMethod !== null && localDeliveryMethod !== void 0 ? localDeliveryMethod : (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.deliveryMethod;
        const requiredFields = (userIsMember || userIsOrganizer) && currentDeliveryMethod == DeliveryMethod.Shipping && !isEmbedded ? [
            "line1",
            "city",
            "state",
            "zip",
            "country",
        ] : [];
        let filteredFields = requiredFields.filter((k) => {
            const address = addressOverride || data.fulfillment.address;
            if (!address)
                return true;
            const v = address[k];
            return !v || v === "";
        });
        if (shippingNotesRequired && !data.fulfillment.shippingNotes)
            filteredFields.push("shippingNotes");
        if ((_b = data.fulfillment.benefit.products) === null || _b === void 0 ? void 0 : _b.length) {
            //@ts-ignore
            let needsVariant = data.fulfillment.benefit.products.reduce((needsVariant, product) => {
                var _a, _b, _c;
                return (needsVariant ||
                    !!((_a = product.variants) === null || _a === void 0 ? void 0 : _a.length) && !((_c = (_b = data.fulfillment.productSelections) === null || _b === void 0 ? void 0 : _b.find((productSelection) => { var _a; return ((_a = productSelection.product) === null || _a === void 0 ? void 0 : _a.id) == product.id; })) === null || _c === void 0 ? void 0 : _c.variant));
            }, false);
            if (needsVariant)
                filteredFields = ["option", ...filteredFields];
        }
        if ((_c = data.fulfillment.benefit.variants) === null || _c === void 0 ? void 0 : _c.length)
            if (!data.fulfillment.variant)
                filteredFields = ["option", ...filteredFields];
        if (fulfillmentDateAdjusted)
            if (!data.fulfillment.fulfillmentDateAdjustmentReason)
                filteredFields = ["reason for changing shipment date", ...filteredFields];
        return Array.from(new Set(filteredFields));
    };
    const handleAddressChange = (fieldName) => (ev) => {
        const newValue = ev.target.value;
        const updatedAddress = Object.assign(Object.assign({}, localAddress), { [fieldName]: newValue });
        setLocalAddress(updatedAddress);
    };
    const cancelSaveFulfillment = async () => {
        // mixpanel.track("Canceled Save Fulfillment")
        if (saveCallback) {
            saveCallback();
        }
    };
    const saveFulfillment = async (addressOverride, fromVariantScreen) => {
        var _a, _b, _c, _d, _e;
        //@ts-ignore
        const blankRequiredFields = ((_b = (_a = data.fulfillment) === null || _a === void 0 ? void 0 : _a.benefit) === null || _b === void 0 ? void 0 : _b.deliveredToYour) == "door" ? checkRequiredFields(!!addressOverride, addressOverride || localAddress) : [];
        if (blankRequiredFields.length) {
            setSaveMessage(_jsxs("div", { className: "text-wfGray-800 border border-wfGray-200 w-full my-2 p-2", children: [_jsx(SvgX, { onClick: closeSaveMessage, className: "ml-4 text-wfGray-600 cursor-pointer transform scale-75 inline-block relative right-05 fill-current", style: { top: "-0.1em" } }), "Error! The following required fields were blank:", _jsx("ul", { children: blankRequiredFields.map((f) => {
                            return _jsx("li", { className: "text-salmon-600", children: f === 'shippingNotes' ? shippingNotesLabel : f });
                        }) })] }));
            setHighlightedFields(blankRequiredFields);
            return;
        }
        startPrimaryAction();
        // mixpanel.track("Saved Fulfillment")
        const originalAddress = addressOverride || localAddress;
        const _f = (originalAddress || {}), { phoneNumber } = _f, addressForMutations = __rest(_f, ["phoneNumber"]);
        if (!isUserUpdateHandledByParent) {
            await updateUser({
                address: Object.keys(addressForMutations).length > 0 ? addressForMutations : undefined,
                phoneNumber: phoneNumber || ((_e = (_d = (_c = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _c === void 0 ? void 0 : _c.membership) === null || _d === void 0 ? void 0 : _d.user) === null || _e === void 0 ? void 0 : _e.phoneNumber)
            });
        }
        const response = await saveSettings({
            refetchQueries: ["ChooseSubscriptionScreen", "ManageFulfillments"],
            awaitRefetchQueries: true,
        }, Object.keys(addressForMutations).length > 0 ? addressForMutations : null, localDeliveryMethod);
        if (response.data) {
            if (!isEmbedded) {
                setSaveMessage(_jsxs("div", { className: "text-wfGray-800 border border-wfGray-200 w-full my-2 p-2", children: [_jsx(SvgX, { onClick: closeSaveMessage, className: "ml-4 text-wfGray-600 cursor-pointer transform scale-75 inline-block relative right-05 fill-current", style: { top: "-0.1em" } }), "Benefit saved! Hit close to continue."] }));
            }
            if (saveCallback) {
                saveCallback();
            }
            if (!fromFulfillmentScreen && !fromVariantScreen) {
                // @ts-ignore
                for (const el of document.getElementsByClassName("wf-button-back")) {
                    if (el instanceof HTMLElement) {
                        el.click();
                    }
                }
            }
        }
        else {
            setSaveMessage(_jsxs("div", { className: "text-wfGray-800 border border-wfGray-200 w-full my-2 p-2", children: [_jsx(SvgX, { onClick: closeSaveMessage, className: "ml-4 text-wfGray-600 cursor-pointer transform scale-75 inline-block relative right-05 fill-current", style: { top: "-0.1em" } }), "Error! Please reload and try again, or contact support@withfriends.co."] }));
        }
        completePrimaryAction();
    };
    const updateStateSelect = (state) => {
        client.writeFragment({
            id: `Fulfillment:${id}`,
            fragment: gql `
        fragment FulfillmentAddressState on Fulfillment {
          address {
            state
          }
        }
      `,
            data: {
                address: {
                    state
                }
            },
        });
    };
    const updateCountrySelect = (country) => {
        client.writeFragment({
            id: `Fulfillment:${id}`,
            fragment: gql `
        fragment FulfillmentAddressCountry on Fulfillment {
          address {
            country
          }
        }
      `,
            data: {
                address: {
                    country
                }
            },
        });
    };
    const addonCheckboxValue = (id) => {
        return ((data === null || data === void 0 ? void 0 : data.fulfillment.addons) || []).some((a) => a.id === id);
    };
    const updatePhoneNumber = (ev) => {
        var _a, _b, _c;
        client.cache.modify({
            id: `User:${(_c = (_b = (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.membership) === null || _b === void 0 ? void 0 : _b.user) === null || _c === void 0 ? void 0 : _c.id}`,
            fields: {
                phoneNumber() {
                    return ev.target.value;
                },
            },
        });
    };
    const updateAddonCheckbox = (id) => {
        return (ev) => {
            // mixpanel.track("Clicked Addon Checkbox")
            if (ev.target.checked) {
                client.cache.modify({
                    id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
                    fields: {
                        addons(existingAddonsRefs = []) {
                            const newAddonRef = {
                                __ref: `Addon:${id}`,
                            };
                            return [...existingAddonsRefs, newAddonRef];
                        },
                    },
                });
            }
            else {
                client.cache.modify({
                    id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
                    fields: {
                        addons(existingAddonsRefs = []) {
                            const filtered = existingAddonsRefs.filter((a) => a.__ref !== `Addon:${id}`);
                            return [...filtered];
                        },
                    },
                });
            }
        };
    };
    if (loading || !data) {
        return _jsx(LoadingScreen, {});
    }
    if (currentShopifyProductVariants === null && currentShopifyVersion == 2 && !currentBusinessHasTooManyProducts) {
        return _jsx(LoadingScreen, {});
    }
    if (error) {
        return _jsx(ErrorScreen, { message: error.message });
    }
    if (!data.fulfillment) {
        return _jsx(ErrorScreen, { message: "404: Fulfillment Not Found" });
    }
    const addonCost = (((_19 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _19 === void 0 ? void 0 : _19.addons) || []).reduce((accum, addon) => {
        return { amount: accum.amount + addon.additionalCost.amount };
    }, { amount: 0 });
    const addonDiscountedCost = (((_20 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _20 === void 0 ? void 0 : _20.addons) || []).reduce((accum, addon) => {
        if (addon.applyDiscount) {
            accum.amount = accum.amount + discountAddon(addon.additionalCost).amount;
        }
        else {
            accum.amount = accum.amount + addon.additionalCost.amount;
        }
        return accum;
    }, { amount: 0 });
    const formatTotal = () => {
        if (addonCost.amount > addonDiscountedCost.amount) {
            return (_jsxs("span", { children: ["\u00A0", _jsx("span", { className: "line-through", style: { textDecoration: "line-through" }, children: formatAmount({ amount: addonCost.amount }) }), _jsxs("span", { className: "font-heavy", children: [" ", formatAmount({ amount: addonDiscountedCost.amount })] })] }));
        }
        else {
            return _jsx("span", { className: "font-heavy", children: formatAmount({ amount: addonCost.amount }) });
        }
    };
    const formatAmount = (a, applyDiscount = false) => {
        const base = `(+$${(a.amount * 0.01).toFixed(2)})`;
        if (retailDiscount.percentage && applyDiscount) {
            const newBase = `(+$${(discountAddon(a).amount * 0.01).toFixed(2)})`;
            return (_jsxs("span", { children: ["\u00A0", _jsx("span", { className: "line-through", style: { textDecoration: "line-through" }, children: base }), _jsxs("span", { className: "font-heavy", children: [" ", newBase] })] }));
        }
        else {
            return _jsxs("span", { children: [" ", base] });
        }
    };
    const updateShippingNotes = (ev) => {
        client.cache.modify({
            id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
            fields: {
                shippingNotes() {
                    return ev.target.value;
                },
            },
        });
    };
    const updateFulfillmentDateAdjustmentReason = (ev) => {
        client.cache.modify({
            id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
            fields: {
                fulfillmentDateAdjustmentReason() {
                    return ev.target.value;
                },
            },
        });
    };
    const updateFulfillmentWithSelectValue = (value) => {
        const normalizedValue = Array.isArray(value) ? value : (value ? [value] : []);
        const fieldValue = normalizedValue.map(eachValue => {
            var _a;
            const platformProductVariantId = eachValue.value.split(",")[0].split("/").pop();
            const isInDefaultCuration = (_a = defaultCuration === null || defaultCuration === void 0 ? void 0 : defaultCuration.platformLineItems) === null || _a === void 0 ? void 0 : _a.find((platformLineItem) => platformLineItem.platformProductVariantId == platformProductVariantId);
            return {
                quantity: 1,
                platformProductVariantId: platformProductVariantId,
                swapped: !isInDefaultCuration
            };
        });
        client.cache.modify({
            id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
            fields: {
                platformLineItems: () => {
                    return fieldValue;
                },
            },
        });
    };
    const handleRemoveOption = useCallback((optionValue) => {
        const updatedSelection = selectedOptions.filter(option => option.value !== optionValue);
        updateFulfillmentWithSelectValue(updatedSelection);
    }, [selectedOptions, updateFulfillmentWithSelectValue]);
    const userIsPurchaser = !!(purchaserId &&
        currentUserId &&
        purchaserId == currentUserId);
    const userIsMember = !!(memberId &&
        currentUserId &&
        memberId == currentUserId);
    const userIsOrganizer = fromFulfillmentScreen;
    const userIsGiftGiver = !!(data.fulfillment.isGift && userIsPurchaser && !userIsMember);
    const isAddonOnlyBenefit = data.fulfillment.benefit.__typename == "SubscriptionBenefit" && data.fulfillment.benefit.isAddonOnly;
    const fulfillmentDateAdjusted = !!originalFulfillmentDate && !originalFulfillmentDate.equals(fulfillmentDate);
    if (errorProducts)
        return _jsx(ErrorScreen, { message: errorProducts.message });
    let shippingNotesLabel = "Shipping Notes";
    let shippingNotesRequired = false;
    let shippingNotesAllowedCharacters = null;
    switch ((_22 = (_21 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _21 === void 0 ? void 0 : _21.benefit) === null || _22 === void 0 ? void 0 : _22.id) {
        case "20508976":
        case "20508979":
        case "20508982":
            shippingNotesLabel = "Kit ID"; // Psychic damage: Dante Labs
            shippingNotesRequired = true;
            shippingNotesAllowedCharacters = "A-Z0-9";
            break;
        default:
            break;
    }
    return (_jsxs("div", { className: "font-sans w-full max-w-xl wf-choose-subscription-screen-wrapper", children: [_jsx("style", { dangerouslySetInnerHTML: { __html: `
        [id^="react-select-"][id$="-listbox"] p.wf-product-variant-addon-price {
          display: initial !important;
        }
        .wf-choose-subscription-screen-wrapper div.[role="button"][aria-label^="Remove"].hidden {
          display: none !important;
        }
      ` } }), headerText ? (_jsx("div", { className: "mb-4 block text-m", children: headerText })) : (data.fulfillment.benefit.addons.length > 0 && data.fulfillment.benefit.__typename == "SubscriptionBenefit" && data.fulfillment.benefit.isAddonOnly ? (_jsxs(_Fragment, { children: [_jsx("h3", { className: "mt-4 mb-4 block pt-4", children: _jsxs("strong", { children: [data.fulfillment.benefit.name, " Add-On"] }) }), _jsxs("p", { className: "mb-4 block text-m", children: ["Want even more from your membership? You can add our ", _jsx("strong", { children: data.fulfillment.benefit.name }), " for an additional charge each ", deliveryPeriod(data.fulfillment.benefit), "."] })] })) : (data.fulfillment.benefit.__typename == "WelcomeGiftBenefit" && isAfterPurchase ? (_jsx(_Fragment, { children: _jsxs("p", { className: "mb-4 block text-m", children: ["As a new member, you'll receive a ", _jsx("strong", { children: data.fulfillment.benefit.name }), " \u2013 a little welcome from us to you!"] }) })) : null)), _jsx("h3", { className: "my-4 block", children: _jsxs("strong", { children: [data.fulfillment.benefit.name, " Preferences"] }) }), (_jsxs(_Fragment, { children: [((_23 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _23 === void 0 ? void 0 : _23.isGift) &&
                        ((_26 = (_25 = (_24 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _24 === void 0 ? void 0 : _24.membership) === null || _25 === void 0 ? void 0 : _25.purchaser) === null || _26 === void 0 ? void 0 : _26.id) && (_jsxs("div", { className: "my-4 block", children: ["Purchaser: ", (_28 = (_27 = data.fulfillment.membership) === null || _27 === void 0 ? void 0 : _27.purchaser) === null || _28 === void 0 ? void 0 : _28.firstName, " ", (_30 = (_29 = data.fulfillment.membership) === null || _29 === void 0 ? void 0 : _29.purchaser) === null || _30 === void 0 ? void 0 : _30.lastName] })), _jsxs("div", { className: "my-4 block", children: ["Member: ", (_31 = data.fulfillment.membership) === null || _31 === void 0 ? void 0 : _31.user.firstName, " ", (_32 = data.fulfillment.membership) === null || _32 === void 0 ? void 0 : _32.user.lastName] })] })), _jsx("div", { className: "block text-wfGray-800", children: userIsOrganizer ? (_jsxs("div", { className: "my-4 block w-full", children: [_jsx("label", { className: "my-2 block", children: benefitFulfillmentText(data.fulfillment.benefit)('fulfillment_date') }), _jsx(DatePicker, { className: "block w-full", returnValue: "start", onChange: (value) => {
                                // This forces the type to be a Date
                                let date;
                                if (value instanceof Date) {
                                    date = value;
                                }
                                else {
                                    return;
                                }
                                client.cache.modify({
                                    id: `Fulfillment:${data === null || data === void 0 ? void 0 : data.fulfillment.id}`,
                                    fields: {
                                        fulfillmentDate() {
                                            return DateTime.fromISO(date.toISOString()).toSQLDate();
                                        },
                                    },
                                });
                            }, value: fulfillmentDate.toJSDate() }), fulfillmentDateAdjusted ? (_jsxs("div", { children: [_jsx("label", { className: "my-4 block", htmlFor: "phoneNumber", children: "Reason for changing fulfillment date (member does not see this)" }), _jsx(TextInput, { id: "fulfillmentDateAdjustmentReason", required: true, value: (_33 = data.fulfillment) === null || _33 === void 0 ? void 0 : _33.fulfillmentDateAdjustmentReason, onChange: updateFulfillmentDateAdjustmentReason, className: "p-2 w-full text-lg", placeholder: "e.g. Member asked for special delivery date", highlighted: _.contains(highlightedFields, "reason for changing shipment date") })] })) : null] })) : (_jsxs(_Fragment, { children: [benefitFulfillmentText(data.fulfillment.benefit)('fulfillment_date'), ": ", fulfillmentDate.toLocaleString()] })) }), !!((_36 = (_35 = (_34 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _34 === void 0 ? void 0 : _34.benefit) === null || _35 === void 0 ? void 0 : _35.addons) === null || _36 === void 0 ? void 0 : _36.length) && (_jsxs(_Fragment, { children: [_jsx("h6", { className: "mb-2 block pt-4", children: _jsx("strong", { children: data.fulfillment.benefit.__typename == "SubscriptionBenefit" && data.fulfillment.benefit.isAddonOnly ? "Add this to your membership:" : "Add something extra:" }) }), _jsx("div", { className: "mb-4 block text-wfGray-600 text-sm", children: "(Select all that apply. Charges will apply to each fulfillment.)" })] })), userIsGiftGiver && ((_39 = (_38 = (_37 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _37 === void 0 ? void 0 : _37.benefit) === null || _38 === void 0 ? void 0 : _38.addons) === null || _39 === void 0 ? void 0 : _39.length) > 0 && (_jsxs("div", { className: "p-4 my-4 border-2 border-salmon-600 prose", children: [_jsx("strong", { className: "font-heavy", children: "Note: This is the only time you can select add-ons for this gift membership." }), " ", "The recipient will not be able to change them."] })), (!!((_40 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _40 === void 0 ? void 0 : _40.isGift) && !userIsPurchaser && !userIsOrganizer && ((_43 = (_42 = (_41 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _41 === void 0 ? void 0 : _41.benefit) === null || _42 === void 0 ? void 0 : _42.addons) === null || _43 === void 0 ? void 0 : _43.length)) ? (_jsxs("div", { className: "p-4 my-4 border-2 border-salmon-600 prose", children: [_jsx("strong", { className: "font-heavy", children: "Note: Add-ons for a gift membership cannot be edited once selected." }), " ", "If you're a gift recipient and you absolutely need to update addons, please get in touch with us at", " ", _jsx("a", { href: "mailto:team@withfriends.co", children: "team@withfriends.co" }), ". Thank you!"] })) : "", (((_45 = (_44 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _44 === void 0 ? void 0 : _44.benefit) === null || _45 === void 0 ? void 0 : _45.addons) || []).map((a) => {
                var _a;
                return (_jsxs("div", { className: "my-2 text-md grid grid-cols-12 gap-2 items-center", children: [_jsx("div", { className: "col-span-1 flex items-center", children: _jsx("input", { className: "inline", type: "checkbox", disabled: !!((_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.isGift) && !userIsPurchaser && !userIsOrganizer, name: `addon-${a.id}`, id: `addon-${a.id}`, value: a.id, checked: addonCheckboxValue(a.id), onChange: updateAddonCheckbox(a.id) }) }), _jsxs("label", { className: "font-sans normal-case col-span-11 flex items-center", htmlFor: `addon-${a.id}`, children: [a.description, "\u00A0", a.additionalCost.amount > 0
                                    ? formatAmount({ amount: a.additionalCost.amount }, a.applyDiscount)
                                    : null] })] }, a.id));
            }), !!((_48 = (_47 = (_46 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _46 === void 0 ? void 0 : _46.benefit) === null || _47 === void 0 ? void 0 : _47.addons) === null || _48 === void 0 ? void 0 : _48.length) && (_jsx(_Fragment, { children: _jsxs("div", { className: "my-4 py-4 font-black block border-wfGray-300 border-t border-b", children: ["Total additional cost: ", formatTotal()] }) })), (!isAddonOnlyBenefit || ((data === null || data === void 0 ? void 0 : data.fulfillment.addons) || []).length > 0) && (_jsxs(_Fragment, { children: [userIsGiftGiver && (_jsxs("div", { className: "p-4 my-4 bg-wfGray-50 border border-wfGray-200", children: [_jsx("p", { children: _jsx("strong", { children: "Note: All of the information below is optional." }) }), _jsx("p", { className: "mt-2", children: "Your gift recipient will receive a welcome letter with a link to fill out these details themselves.  If you know the answers, it'll save them a step!" })] })), data.fulfillment.benefit.products.filter((product) => product.variants.length).map((product) => {
                        return (_jsx(ChooseProductSelection, { product: product, productSelection: data.fulfillment.productSelections.find((productSelection) => productSelection.product.id == product.id), fulfillmentId: id }, product.id));
                    }), currentShopifyVersion == 2 && !currentBusinessHasTooManyProducts && ((_49 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _49 === void 0 ? void 0 : _49.curated) && ((_51 = (_50 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _50 === void 0 ? void 0 : _50.benefit) === null || _51 === void 0 ? void 0 : _51.__typename) == "SubscriptionBenefit" && ((_54 = (_53 = (_52 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _52 === void 0 ? void 0 : _52.benefit) === null || _53 === void 0 ? void 0 : _53.business) === null || _54 === void 0 ? void 0 : _54.enableProductSwapping) &&
                        _jsxs(_Fragment, { children: [_jsx("h4", { className: "my-4 block pt-4", children: "Customize your subscription box" }), _jsxs("p", { className: "my-2", children: [isAddonOnly ? "Add products to your box.  " : "Adjust the products in your box.  ", "Some changes may affect the final price."] }), _jsxs("div", { id: productSearchHelperId, className: "mb-3 flex items-start gap-2 px-3 py-2 text-sm text-black", style: { backgroundColor: "#f6f4f4" }, children: [_jsx(HiOutlineInformationCircle, { "aria-hidden": true, size: 18, className: "mt-0.5 text-black" }), _jsxs("div", { children: [_jsx("span", { className: "font-medium text-black", children: "Tip:" }), " Start typing to search by product name or vendor. Use the remove button next to a product in your box to take it out."] })] }), _jsx("div", { ref: selectContainerRef, children: _jsx(Select, { ref: selectInstanceRef, windowThreshold: 100, minMenuHeight: 480, id: `platformId-${id}`, "aria-label": "Customize subscription products", "aria-describedby": productSearchHelperId, formatOptionLabel: ({ label }) => label, components: { Control, Placeholder, NoOptionsMessage, MenuList, Option }, value: selectedOptions, isMulti: true, delimiter: ",", closeMenuOnSelect: true, controlShouldRenderValue: false, onChange: updateFulfillmentWithSelectValue, onMenuOpen: handleMenuOpen, onMenuClose: handleMenuClose, options: options, filterOption: customFilterSelectOption, placeholder: "Search products", isClearable: false, styles: {
                                            control: (provided, state) => (Object.assign(Object.assign({}, provided), { borderWidth: state.isFocused ? 2 : 1, borderColor: state.isFocused ? "#ff877b" : "#d1d5db", boxShadow: "none", paddingLeft: "4px", paddingRight: "8px", minHeight: "64px", cursor: "text", fontSize: "16px" })),
                                            valueContainer: (provided) => (Object.assign(Object.assign({}, provided), { padding: "8px 0" })),
                                            placeholder: (provided) => (Object.assign(Object.assign({}, provided), { color: "#6b7280", marginLeft: 0, fontSize: "16px" })),
                                            input: (provided) => (Object.assign(Object.assign({}, provided), { margin: 0, padding: 0, color: "#111827", cursor: "text", fontSize: "16px" })),
                                            option: (provided, state) => (Object.assign(Object.assign({}, provided), { padding: "12px 14px", backgroundColor: state.isSelected
                                                    ? "#ffdad6"
                                                    : state.isFocused
                                                        ? "#fff7f5"
                                                        : "#ffffff", color: "#111827", cursor: isMobileViewport ? "default" : "pointer" })),
                                            menu: (provided) => (Object.assign(Object.assign({}, provided), { zIndex: 10 })),
                                            menuList: (provided) => (Object.assign(Object.assign({}, provided), { paddingTop: 0 })),
                                            dropdownIndicator: (provided) => (Object.assign(Object.assign({}, provided), { cursor: "pointer" })),
                                        }, theme: (theme) => (Object.assign(Object.assign({}, theme), { borderRadius: 0, colors: Object.assign(Object.assign({}, theme.colors), { primary: "#ff877b", primary25: "#ffdad6", primary50: "#ffdad6", primary75: "#ffdad6", neutral20: "#d1d5db" }) })) }) }), !!selectedOptions.length && (_jsxs("div", { className: "mt-4 border border-wfGray-200 bg-white", children: [_jsxs("div", { className: "flex items-center gap-2 border-b border-wfGray-200 px-3 py-2 text-sm font-semibold text-wfGray-900", style: { backgroundColor: "#f6f4f4" }, children: [_jsx(HiOutlineCube, { "aria-hidden": true, className: "text-black", size: 18 }), _jsx("span", { children: "Products currently in your box" })] }), _jsx("ul", { className: "divide-y divide-wfGray-200", children: selectedOptions.map((option) => (_jsxs("li", { className: "flex items-center justify-between gap-3 px-3 py-3 text-sm", children: [_jsxs("div", { className: "flex items-center gap-3", children: [option.imageUrl ? (_jsx("img", { src: option.imageUrl, alt: option.displayName, className: "h-12 w-12 border border-wfGray-200 object-cover", style: { borderRadius: 0 } })) : (_jsx("div", { className: "h-12 w-12 border border-wfGray-200", style: { backgroundColor: "#f6f4f4" } })), _jsx("span", { className: "font-medium text-wfGray-900", children: option.displayName })] }), _jsx("button", { type: "button", onClick: () => handleRemoveOption(option.value), "aria-label": `Remove ${option.displayName} from your box`, className: "flex h-8 w-8 items-center justify-center text-base font-semibold text-black transition hover:text-wfGray-700", children: _jsx("span", { "aria-hidden": true, children: "\u00D7" }) })] }, option.value))) })] })), discountedAddonAmount ?
                                    _jsxs("p", { className: "my-2" + (discountedPrice > discountedDefaultCurationValue ? " text-wfRed-600" : ""), children: ["By clicking \"Save,\" you'll confirm your selections and any additional charges of ", formattedPrice(majorToMinorPrice(discountedAddonAmount)), " will be applied on your fulfillment date. ", (retailDiscount === null || retailDiscount === void 0 ? void 0 : retailDiscount.percentage) > 0 ? _jsxs(_Fragment, { children: ["This amount reflects your ", retailDiscount === null || retailDiscount === void 0 ? void 0 : retailDiscount.percentage, "% member discount on the added items."] }) : null] }) : (hasSelectionChanged() ?
                                    _jsx("p", { className: "my-2", children: "By clicking \"Save,\" you'll confirm your selections. There are no additional charges for the products you've added to your subscription box." }) : null), _jsx(Button, { className: "mt-4", variant: ButtonVariant.Outlined, onClick: resetCuration, children: "Reset curation" })] }), (() => {
                        var _a, _b, _c, _d;
                        const benefit = (_a = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _a === void 0 ? void 0 : _a.benefit;
                        if (!benefit)
                            return null;
                        // Determine available options
                        const shippingEnabled = !!benefit.shippingEnabled;
                        const pickupEnabled = !!benefit.pickupEnabled;
                        // If neither is enabled, show nothing
                        if (!shippingEnabled && !pickupEnabled)
                            return null;
                        // Build options array
                        const deliveryOptions = [];
                        if (shippingEnabled) {
                            let shippingLabel = "Ship to my address";
                            if (benefit.shippingCost && benefit.shippingCost.amount > 0) {
                                shippingLabel += ` (+${formattedPrice(benefit.shippingCost.amount)} shipping)`;
                            }
                            deliveryOptions.push({ value: "SHIPPING", label: shippingLabel });
                        }
                        if (pickupEnabled) {
                            deliveryOptions.push({ value: "PICKUP", label: "Pick up in-store" });
                        }
                        // Determine current value
                        // Assume data.fulfillment.deliveryMethod is either "shipping" or "pickup"
                        // If not present, default to first available
                        const currentDeliveryMethod = (_c = localDeliveryMethod !== null && localDeliveryMethod !== void 0 ? localDeliveryMethod : (_b = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _b === void 0 ? void 0 : _b.deliveryMethod) !== null && _c !== void 0 ? _c : (_d = deliveryOptions[0]) === null || _d === void 0 ? void 0 : _d.value;
                        // Handler for changing delivery method
                        const handleDeliveryMethodChange = (e) => {
                            const newValue = e.target.value;
                            setLocalDeliveryMethod(newValue);
                            onDeliveryMethodChange(newValue);
                        };
                        const deliveryPreferenceIsDisabled = (currentShopifyVersion == 2 && !data.fulfillment.benefit.business.forceStripePayments && !subscriptionBenefitHasShippingIncluded) || deliveryOptions.length == 1;
                        // Otherwise, show select
                        return (_jsxs("div", { className: "my-4 block", children: [_jsx("label", { className: "my-4 pt-4 block font-normal text-xl leading-relaxed", htmlFor: "delivery-method-select", children: "Delivery Preference" }), _jsx("select", { id: "delivery-method-select", className: "p-2 w-full border border-wfGray-300 focus:border-salmon-600 focus:border-2 text-lg", value: currentDeliveryMethod, onChange: handleDeliveryMethodChange, disabled: deliveryPreferenceIsDisabled, style: deliveryPreferenceIsDisabled ? {
                                        backgroundColor: "#eee",
                                        opacity: .6,
                                    } : {}, children: deliveryOptions.map((opt) => (_jsx("option", { value: opt.value, children: opt.label }, opt.value))) })] }));
                    })(), (!isEmbedded && data.fulfillment.benefit.variants || []).length > 0 ? (_jsxs(_Fragment, { children: [_jsx("h4", { className: "my-4 block pt-4", children: "Options" }), _jsxs("select", { onChange: updateVariantSelect, value: (_56 = (_55 = data.fulfillment) === null || _55 === void 0 ? void 0 : _55.variant) === null || _56 === void 0 ? void 0 : _56.id, className: "p-2 w-full border border-wfGray-300 focus:border-salmon-600 focus:border-2 text-lg", children: [_jsx("option", { children: "Select a Subscription Option!" }), (_59 = (_58 = (_57 = data.fulfillment) === null || _57 === void 0 ? void 0 : _57.benefit) === null || _58 === void 0 ? void 0 : _58.variants) === null || _59 === void 0 ? void 0 : _59.map((variant) => {
                                        return _jsx(VariantOption, Object.assign({}, variant), variant.id);
                                    })] })] })) : null, _jsx("div", { className: "my-8 block" }), 
                    //@ts-ignore
                    !isEmbedded && ((_60 = data.fulfillment.benefit) === null || _60 === void 0 ? void 0 : _60.deliveredToYour) == "door" && localDeliveryMethod == DeliveryMethod.Shipping && (_jsxs(_Fragment, { children: [_jsxs(ExpandableArea, { title: "Shipping Address", expandedStart: true, children: [_jsx("div", { className: "text-wfRed-600 text-base my-2", children: "* Required" }), _jsx(AddressInput, { label: "Shipping Name", placeholder: "", fieldName: "shippingName", value: (localAddress === null || localAddress === void 0 ? void 0 : localAddress.shippingName) || "", onChange: handleAddressChange("shippingName") }), _jsx(AddressInput, { label: "Address Line 1", fieldName: "line1", value: (localAddress === null || localAddress === void 0 ? void 0 : localAddress.line1) || "", onChange: handleAddressChange("line1"), required: userIsMember || userIsOrganizer, placeholder: "123 Main St.", highlighted: _.contains(highlightedFields, "line1") }), _jsx(AddressInput, { label: "Address Line 2", placeholder: "", fieldName: "line2", value: (localAddress === null || localAddress === void 0 ? void 0 : localAddress.line2) || "", onChange: handleAddressChange("line2") }), _jsxs("div", { className: "sm:grid sm:grid-cols-4 sm:gap-4", children: [_jsx("div", { className: "sm:col-span-2", children: _jsx(AddressInput, { label: "City", fieldName: "city", value: (localAddress === null || localAddress === void 0 ? void 0 : localAddress.city) || "", onChange: handleAddressChange("city"), required: userIsMember || userIsOrganizer, placeholder: "New York", highlighted: _.contains(highlightedFields, "city") }) }), _jsx("div", { className: "sm:col-span-2", children: _jsx(AddressInput, { label: "ZIP", fieldName: "zip", value: (localAddress === null || localAddress === void 0 ? void 0 : localAddress.zip) || "", onChange: handleAddressChange("zip"), required: userIsMember || userIsOrganizer, placeholder: "12345", highlighted: _.contains(highlightedFields, "zip") }) })] }), _jsxs("div", { children: [_jsxs("label", { className: "my-4 block w-full", htmlFor: "state", children: ["State ", _jsx("span", { className: "text-wfRed-600", children: "*" })] }), _jsx(RegionDropdown, { name: "state", classes: `my-4 block w-full p-2 border focus:border-salmon-600 focus:border-2 text-lg ${_.contains(highlightedFields, "state")
                                                    ? "border-salmon-600"
                                                    : "border-wfGray-300"}`, country: (localAddress === null || localAddress === void 0 ? void 0 : localAddress.country) || "United States", value: localAddress === null || localAddress === void 0 ? void 0 : localAddress.state, onChange: (val) => handleAddressChange("state")({ target: { value: val } }) })] }), _jsxs("div", { children: [_jsxs("label", { className: "my-4 block w-full", htmlFor: "country", children: ["Country ", _jsx("span", { className: "text-wfRed-600", children: "*" })] }), _jsx(CountryDropdown, { name: "country", classes: `my-4 block w-full p-2 border focus:border-salmon-600 focus:border-2 text-lg ${_.contains(highlightedFields, "country")
                                                    ? "border-salmon-600"
                                                    : "border-wfGray-300"}`, value: (localAddress === null || localAddress === void 0 ? void 0 : localAddress.country) || "United States", onChange: (val) => handleAddressChange("country")({ target: { value: val } }) })] })] }), _jsxs("div", { children: [_jsx("label", { className: "my-4 block", htmlFor: "phoneNumber", children: "Phone Number" }), _jsx(TextInput, { id: "phoneNumber", value: (_63 = (_62 = (_61 = data.fulfillment) === null || _61 === void 0 ? void 0 : _61.membership) === null || _62 === void 0 ? void 0 : _62.user) === null || _63 === void 0 ? void 0 : _63.phoneNumber, onChange: updatePhoneNumber, className: "p-2 w-full text-lg" })] }), _jsxs("div", { children: [_jsxs("label", { className: "my-4 block", htmlFor: "shippingNotes", children: [shippingNotesLabel, " ", shippingNotesRequired && _jsx("span", { className: "text-wfRed-600", children: "*" })] }), ((_65 = (_64 = data.fulfillment) === null || _64 === void 0 ? void 0 : _64.benefit) === null || _65 === void 0 ? void 0 : _65.shippingNotesInstructions) && (_jsx("p", { className: "my-4", dangerouslySetInnerHTML: {
                                            __html: (_68 = (_67 = (_66 = data.fulfillment) === null || _66 === void 0 ? void 0 : _66.benefit) === null || _67 === void 0 ? void 0 : _67.shippingNotesInstructions) === null || _68 === void 0 ? void 0 : _68.replace(/\n/g, '<br>')
                                        } })), _jsx(TextAreaInput, { id: "shippingNotes", value: data.fulfillment.shippingNotes || "", style: { height: "5rem" }, onChange: updateShippingNotes, className: "p-2 w-full text-lg", allowedCharacters: shippingNotesAllowedCharacters })] })] })), (((_70 = (_69 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _69 === void 0 ? void 0 : _69.benefit) === null || _70 === void 0 ? void 0 : _70.__typename) == "SubscriptionBenefit" && !isAfterPurchase && !isEmbedded) && (_jsxs("div", { className: "my-4 text-md grid grid-cols-12 gap-2", children: [_jsxs("label", { className: "flex font-sans normal-case col-span-11 cursor-pointer", htmlFor: `${namespace}_user-save-to-future-false`, children: [_jsx("input", { type: "radio", className: "accent-salmon-700 shadow-none height-4 mr-4", name: `${namespace}_user-save-to-future`, id: `${namespace}_user-save-to-future-false`, value: "false", checked: !saveToFutureFulfillments, onChange: (e) => {
                                            saveToFutureFulfillmentsVar(e.target.value == "true");
                                        } }), "Update only this box."] }), _jsxs("label", { className: "flex font-sans normal-case col-span-11 cursor-pointer", htmlFor: `${namespace}_user-save-to-future-true`, children: [_jsx("input", { type: "radio", name: `${namespace}_user-save-to-future`, id: `${namespace}_user-save-to-future-true`, className: "accent-salmon-700 shadow-none height-4 mr-4", value: "true", checked: saveToFutureFulfillments, onChange: (e) => {
                                            saveToFutureFulfillmentsVar(e.target.value == "true");
                                        } }), "Use these settings for all upcoming boxes", userIsOrganizer && " for this member (except for the fulfillment date)", ((_71 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _71 === void 0 ? void 0 : _71.curated) && ". Note: Customized products will only apply to this box."] })] }))] })), saveMessage, !isEmbedded && _jsx(Button, { className: "my-4", variant: ButtonVariant.Salmon, disabled: primaryActionLoading, onClick: saveFulfillment, children: (((_73 = (_72 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _72 === void 0 ? void 0 : _72.benefit) === null || _73 === void 0 ? void 0 : _73.claimable) && !((_74 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _74 === void 0 ? void 0 : _74.claimedAt) && ((_75 = data === null || data === void 0 ? void 0 : data.fulfillment) === null || _75 === void 0 ? void 0 : _75.curated)) ? "Save settings and claim shipment" : "Save settings" }), showCancelButton && (_jsx(Button, { className: "my-4", variant: ButtonVariant.Outlined, disabled: primaryActionLoading, onClick: cancelSaveFulfillment, children: "Cancel" }))] }));
});
export default ChooseSubscriptionScreen;
